import { Card } from '../components/Card'
import { useLearnMode } from '../lib/learn-mode'
import { useTranslation } from 'react-i18next'

interface Term {
  term: string
  def: string
}

interface Section {
  title: string
  terms: Term[]
}

const GLOSSARY: Section[] = [
  {
    title: 'Cash & profit',
    terms: [
      { term: 'Operating cash', def: "Real money in minus real money out, excluding customer deposits passing through. The truth number — tells you if you're actually making money." },
      { term: 'Deposit churn', def: "Money customers deposit and then spend on labels. Flows through ByeLabel but isn't profit." },
      { term: 'Accrual profit', def: 'Profit earned (regardless of whether collected yet). Different from cash because some customers pay later.' },
      { term: 'Margin', def: 'Profit per shipment. Selling price minus carrier cost.' },
      { term: 'Gross profit', def: 'All margin added together for the period.' },
      { term: 'Days of runway', def: "If no money came in, how many days you'd last on current cash + spending pace." },
    ],
  },
  {
    title: 'Customers & revenue',
    terms: [
      { term: 'ARPU', def: 'Average Revenue Per User. Money earned ÷ number of customers in the period.' },
      { term: 'LTV', def: 'Lifetime Value. Total profit one customer brought over their entire relationship.' },
      { term: 'MRR', def: 'Monthly Recurring Revenue. Predictable subscription income each month.' },
      { term: 'Customer deposit float', def: "Customer money you're holding. Owed back if they leave." },
      { term: 'Churn', def: 'Customer stops using the service. Churned customers no longer generate revenue.' },
      { term: 'Concentration risk', def: 'How dependent the business is on a few customers. Higher = more risk.' },
    ],
  },
  {
    title: 'Billing & receivables',
    terms: [
      { term: 'AR', def: 'Accounts Receivable. Money customers owe you on invoices not yet paid.' },
      { term: 'AP', def: 'Accounts Payable. Money you owe vendors (carriers, contractors) not yet paid.' },
      { term: 'Aging', def: 'How long an invoice has been overdue. 30+ days = concerning.' },
      { term: 'Net 15 / Net 30', def: 'Customer has 15 (or 30) days to pay after receiving the invoice.' },
      { term: 'Credit-based customer', def: 'Customer who pays after invoice (vs deposit customers who prepay).' },
      { term: 'Deposit-based customer', def: 'Customer who prepays into a balance; labels and adjustments deducted from that balance.' },
      { term: 'Adjustment / rebill', def: 'A charge from the carrier we pass to the customer (reweigh, surcharge, dim correction).' },
      { term: 'Unbilled adjustment', def: "An adjustment we owe to charge the customer but haven't yet. Money sitting unclaimed." },
    ],
  },
  {
    title: 'Shipping & pricing',
    terms: [
      { term: 'Volume velocity', def: "Labels per day. Speed at which you're shipping." },
      { term: 'Used label', def: 'Label that actually shipped (appears in a carrier invoice). Unused labels often refund.' },
      { term: 'Pareto', def: 'The 80/20 idea: usually ~20% of customers drive ~80% of profit.' },
      { term: 'Break-even', def: "The point where a price cut's lost margin is exactly recovered by the volume gain." },
      { term: 'Required volume multiplier', def: 'How much MORE you need to sell after a discount to keep the same total profit.' },
      { term: 'Elasticity', def: "How much volume changes when you change the price. We don't have data to predict this; you judge it." },
    ],
  },
  {
    title: 'Partners & ops',
    terms: [
      { term: 'Affiliate / referrer', def: "Someone who refers customers to ByeLabel. Earns a cut on their referred customers' profit/volume." },
      { term: 'Attribution', def: "Saying 'this customer was referred by that affiliate'. Once attributed, commission flows." },
      { term: 'Commission', def: 'The cut paid to an affiliate. Can be % of profit OR cents per label.' },
      { term: 'Shareholder loan', def: 'Money an owner injected into the business. Usually paid back later, not revenue.' },
      { term: 'Dispute', def: 'Carrier overcharged us; we dispute it and may recover money.' },
    ],
  },
  {
    title: 'Banking & cash sources',
    terms: [
      { term: 'ACH', def: 'Bank-to-bank electronic transfer in the US. Cheap, 1-3 day delivery.' },
      { term: 'Wire', def: 'Same-day bank transfer. More expensive, used for large amounts.' },
      { term: 'Zelle / QuickPay', def: 'Free instant bank transfer between US accounts.' },
      { term: 'Stripe payout', def: 'Money Stripe collected from cards, transferring to your Chase. Usually 2 days delayed.' },
      { term: 'Reconciliation', def: 'Matching what one system says (Stripe) against what another shows (Chase). Both should agree.' },
      { term: 'Error reversal', def: 'Money came in by mistake and was sent back. Nets to zero, ignore in analysis.' },
    ],
  },
]

export function GlossaryView() {
  const { t } = useTranslation()
  const { learnMode, toggle } = useLearnMode()

  return (
    <div className="space-y-5">
      <Card className="p-7">
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">{t('glossary.title')}</div>
            <div className="mt-2 text-[28px] font-bold text-ink-900 leading-none">{t('glossary.subtitle')}</div>
            <div className="mt-2 text-[13px] text-ink-500">
              Every jargon term used in the dashboard, explained without other jargon.
              If a term here isn't clear, tell us and we'll rewrite it.
            </div>
          </div>
          <button
            onClick={toggle}
            className="px-3 py-1.5 rounded-lg text-sm font-medium border border-ink-200 bg-white hover:bg-ink-50"
          >
            {learnMode ? t('glossary.learn_mode_on') : t('glossary.learn_mode_off')} Learn mode
          </button>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        {GLOSSARY.map(section => (
          <Card key={section.title} className="p-5">
            <div className="text-[13px] font-bold text-royal-700 mb-3">{section.title}</div>
            <table className="w-full text-[13px]">
              <tbody>
                {section.terms.map(t => (
                  <tr key={t.term} className="border-b border-ink-100 last:border-0">
                    <td className="py-2.5 pr-3 align-top font-semibold text-ink-800 whitespace-nowrap w-1/3">
                      {t.term}
                    </td>
                    <td className="py-2.5 text-ink-600 leading-relaxed">{t.def}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))}
      </div>

      <Card className="p-5 bg-amber-50 border-amber-200">
        <div className="text-[13px] font-bold text-amber-900 mb-2">{t('glossary.two_ways_title')}</div>
        <div className="grid grid-cols-2 gap-4 text-[13px] text-amber-900">
          <div>
            <span className="font-semibold">Hover the ⓘ icons</span> wherever you see them next to a label — quick one-line definition appears, no clutter.
          </div>
          <div>
            <span className="font-semibold">Turn on Learn mode (top bar)</span> — yellow inline boxes appear under every metric with deeper context. Off by default to keep daily-use clean.
          </div>
        </div>
      </Card>
    </div>
  )
}
